/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQueryClient } from '@tanstack/react-query';
import { hexToBigInt, toBytes } from 'viem';
import { type Address } from 'wagmi';

import { SignatureKind, useAppDispatch, useWalletConnect, useWallets } from '@pkg/hooks';
import { walletAddressWithPrefix } from '@pkg/hooks/wallets/useConnectAnyWallet/useConnectAnyWallet';
import { Web3Chains, Web3WagmiConnectors } from '@pkg/providers/Web3Provider/types';
import { type MarketplaceCollectibleIdentifier, type WagmiCallbacks } from '@pkg/types';

import { CollectionChainType, SupportedTokens } from '@app/client';
import { callExecuteStep, callPrepareOrder } from '@app/client/resources/marketplace';
import { addNotification } from '@app/store/actions/notification';

import {
    type NewOrder,
    type NewOrderSettings,
    type PrepareNewOrder,
    type Step,
    type StepItem,
    StepItemStatus,
    StepKind,
} from './types';

interface NewOrderCallbacks {
    onComplete?: () => void;
    onError?: () => void;
}

interface NewOrderConfig extends NewOrderCallbacks {
    settings: NewOrderSettings;
    order: PrepareNewOrder;
}

export interface OrderOptions extends WagmiCallbacks {}

export function useOrder({ onError }: OrderOptions = {}) {
    const dispatch = useAppDispatch();

    const { connectAsync, disconnectAsync, isConnected, signTypedDataAsync, signMessageAsync, sendTransaction } =
        useWalletConnect({
            onError,
        });
    const queryClient = useQueryClient();
    const { getConnectedWallet } = useWallets();

    function getChain(chain: CollectionChainType) {
        // TODO: Why does this need to be all uppercase?
        return chain.toUpperCase() as PrepareNewOrder['chain'];
    }

    function getCollectibleIdentifier(collectibleIdentifier: MarketplaceCollectibleIdentifier) {
        const chain = collectibleIdentifier.chain.toLowerCase();
        return `${chain}.${collectibleIdentifier.contractAddress}.${collectibleIdentifier.tokenId}`;
    }

    async function newOrder({ settings, order, onComplete, onError }: NewOrderConfig) {
        try {
            await prepareOrder(
                {
                    ...settings,
                    ...order,
                },
                {
                    onComplete,
                    onError,
                },
            );
        } catch (error) {
            onError?.();
        }
    }

    async function prepareOrder(prepareOrder: NewOrder, callbacks?: NewOrderCallbacks) {
        const cache = ['prepare-listing', prepareOrder.item.collectibleIdentifier];

        const wallet = await getConnectedWallet(CollectionChainType.Ethereum);

        const prepareOrderResponse = await queryClient.fetchQuery(
            cache,
            ({ signal }) =>
                callPrepareOrder({
                    variables: {
                        account: walletAddressWithPrefix(SupportedTokens.Ethereum, wallet),
                        allMarkets: prepareOrder.allMarkets,
                        chain: prepareOrder.chain,
                        items: [prepareOrder.item],
                        side: prepareOrder.side,
                        type: prepareOrder.type,
                    },
                    settings: { signal },
                }),
            {
                retry: false,
            },
        );

        if (prepareOrderResponse instanceof Error) {
            // eslint-disable-next-line no-console
            console.debug('GraphQL Server Error', prepareOrderResponse.message);
            dispatch(addNotification({ message: 'Error creating new listing.', severity: 'error' }));
            throw prepareOrderResponse;
        }

        processOrderSteps(prepareOrderResponse, callbacks);
    }

    function processOrderSteps(prepareOrderResponse: any, callbacks?: NewOrderCallbacks) {
        if (prepareOrderResponse.origin.steps) {
            prepareOrderResponse.origin.steps.forEach((step: any) =>
                processStep(step, prepareOrderResponse.orderId, callbacks),
            );
        }
    }

    function processStep(step: Step, orderId: string, callbacks?: NewOrderCallbacks) {
        switch (step.kind) {
            case StepKind.SIGNATURE: {
                handleSignMessage(step, orderId, callbacks);
                break;
            }

            case StepKind.TRANSACTION: {
                handleSendTransaction(step, callbacks);
                break;
            }

            default:
                throw new Error(`Unknown step kind "${step.kind}"`);
        }
    }

    async function handleSendTransaction(step: Step, callbacks?: NewOrderCallbacks) {
        if (isConnected) {
            await disconnectAsync();
        }

        const currentAccount = await connectAsync(Web3WagmiConnectors.Metamask, Web3Chains.Ethereum);

        if (!currentAccount || !step.items) {
            callbacks?.onError?.();
            return;
        }

        // TODO: Iterate items
        const item = step.items.shift();
        if (!item || !item.data) return;

        if (item.status === StepItemStatus.COMPLETE) {
            callbacks?.onError?.();
            return;
        }

        const transaction = await sendTransaction({
            account: (currentAccount ?? item.data.from) as Address,
            chainId: 1,
            data: item.data.data as Address,
            to: item.data.to,
            value: hexToBigInt((item.data.value as any) || 0),
            ...(item.data.maxFeePerGas && {
                maxFeePerGas: hexToBigInt(item.data.maxFeePerGas as any),
            }),
            ...(item.data.maxPriorityFeePerGas && {
                maxPriorityFeePerGas: hexToBigInt(item.data.maxPriorityFeePerGas as any),
            }),
            ...(item.data.gas && {
                gas: hexToBigInt(item.data.gas as any),
            }),
        });

        callbacks?.onComplete?.();
        return transaction;
    }

    async function handleSignMessage(step: any, orderId: string, callbacks?: NewOrderCallbacks) {
        if (isConnected) {
            await disconnectAsync();
        }

        const currentAccount = await connectAsync(Web3WagmiConnectors.Metamask, Web3Chains.Ethereum);
        if (!currentAccount) return;

        // TODO: Iterate items
        const item = step.items.shift();
        if (!item) return;

        if (item.status === StepItemStatus.COMPLETE) return;

        const signature = await signMessage(item, currentAccount as Address);
        if (!signature) {
            callbacks?.onError?.();
            return;
        }

        // TODO: We might need a better identifier here.
        const cache = ['submit-order'];

        const submitOrderResponse = await queryClient.fetchQuery(
            cache,
            ({ signal }) =>
                callExecuteStep({
                    variables: {
                        orderId,
                        step: {
                            submitOrder: {
                                signature,
                                body: item.data.post.body,
                            },
                        },
                    },
                    settings: { signal },
                }),
            {
                retry: false,
            },
        );

        if (submitOrderResponse instanceof Error) {
            // eslint-disable-next-line no-console
            console.debug('GraphQL Server Error', submitOrderResponse.message);
            dispatch(addNotification({ message: 'Error creating new listing.', severity: 'error' }));
            callbacks?.onError?.();
            return;
        }

        callbacks?.onComplete?.();
    }

    async function signMessage(item: StepItem, account: Address) {
        const signatureData = item.data.sign;
        let signature: string | undefined;

        if (signatureData.signatureKind === SignatureKind.EIP191) {
            // If message is a hash, convert it to raw bytes
            const message = signatureData.message.match(/0x[0-9a-fA-F]{64}/)
                ? toBytes(signatureData.message)
                : signatureData.message;
            signature = await signMessageAsync({ message });
        } else if (signatureData.signatureKind === SignatureKind.EIP712) {
            signature = await signTypedDataAsync({
                account,
                domain: signatureData.domain as any,
                types: signatureData.types as any,
                primaryType: signatureData.primaryType,
                message: signatureData.value,
            });
        }
        return signature;
    }

    return {
        getChain,
        getCollectibleIdentifier,
        newOrder,
    };
}
